import React from "react";
import styled, { css, keyframes } from "styled-components";
import light_record_svg from "../../imgs/light_record_with_pin.svg";
import record_svg from "../../imgs/record.svg";

const CircleContainer = styled.div`
  display: inline-block;
  border-radius: 100%;
  position: relative;
`;

const RecordContainer = styled.div`
  position: absolute;
  height: 100%;
  background: url(${(props) =>
      props.theme.themeType === "dark" ? record_svg : light_record_svg})
    center no-repeat;
  background-size: 90%;
  width: 100%;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const indeterminateSpin = keyframes`
from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const StyledCircle = styled("circle")<{ indeterminate: boolean }>`
  transition: all 0.3s ease-in;
  transform-origin: 50% 50%;
  ${(props) =>
    props.indeterminate &&
    css`
      animation-name: ${indeterminateSpin};
      animation-duration: 2s;
      animation-iteration-count: infinite;

    `}
`;

const GradientCircleProgressbar: React.FC<GradientCircleProgressbarProps> = ({
  percentage,
  actualWidth,
  strokeWidth,
  primaryColor,
  secondaryColor,
  fill,
  loading
}) => {
  const PI = 3.14;
  const width = actualWidth ? actualWidth : 200;
  const R = width / 2 - (strokeWidth as number) * 2;
  let primaryColorArray = primaryColor as Array<string>;

  let circumference = 2 * PI * R;
  let offset =
    percentage === null || percentage === undefined
      ? (loading ? -(circumference - (20 / 100) * circumference) :  -(circumference -1 * circumference))
      : -(circumference - (percentage / 100) * circumference);
  let gradientId =
    `${primaryColorArray[0]}${primaryColorArray[1]}${primaryColorArray[2]}`.replace(
      /#/g,
      ""
    );

  return (
    <CircleContainer
      className="react-super-progressbar__base"
      style={{
        height: `${width}px`,
        width: `${width}px`,
      }}
    >
      <RecordContainer />

      <svg
        width="100%"
        height="100%"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
      >
        {percentage === null || percentage === undefined ? (
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={"white"} />
          </linearGradient>
        ) : (
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="10%" stopColor={primaryColorArray[2]} />
            <stop offset="50%" stopColor={primaryColorArray[1]} />
            <stop offset="100%" stopColor={primaryColorArray[0]} />
          </linearGradient>
        )}

        <circle
          strokeWidth={strokeWidth}
          fill="transparent"
          r={R}
          cx={width / 2}
          cy={width / 2}
          stroke={secondaryColor}
          strokeDasharray={`${circumference} ${circumference}`}
        />
        <StyledCircle
          indeterminate={percentage === undefined || percentage === null}
          strokeWidth={strokeWidth}
          fill={fill}
          r={R}
          cx={width / 2}
          cy={width / 2}
          stroke={`url(#${gradientId})`}
          strokeLinecap="round"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={offset}
        />
      </svg>
    </CircleContainer>
  );
};

interface GradientCircleProgressbarProps {
  percentage?: number;
  actualWidth?: number;
  strokeWidth?: number;
  primaryColor?: Array<string>;
  secondaryColor?: string;
  fill?: string;
  loading?: boolean;
}

const defaultProps = {
  actualWidth: 200,
  strokeWidth: 5,
  primaryColor: ["#22B5F4", "#DEEED2", "#764BFF"],
  secondaryColor: "transparent",
  fill: "transparent",
};
GradientCircleProgressbar.defaultProps = defaultProps;

export default GradientCircleProgressbar;
